import ProgramBannerButton from "./ProgramBannerButton.tsx";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import useTermStoreMetrics from "../useTermStoreMetrics.tsx";
import { useCallback, useEffect, useState } from "react";
import Confetti from "../../../../../common/core-ui/Confetti.tsx";
import usePromiseFetcher from "../../../../../common/core-ui/usePromiseFetcher.ts";
import { useStoreMultiCartFetcher } from "../../actions/storeMultiCartAction.ts";
import useToasts from "../../../../root/core-ui/toast/useToasts.ts";
import Toast from "../../../../../common/core-ui/Toast.tsx";
import { clsx } from "clsx";
import { useNavigate } from "react-router-dom";
import useVirtualStore from "../../../common/virtual-store/useVirtualStore.tsx";
import { useBrandLoaderData } from "../../../../root/rootLoader.ts";
import { useProgramDetails } from "../coursesLoader.ts";
import { logEvent } from "common/api/monitoring/index.ts";

export default function ProgramBannerAddAllButton({
  isLinkVariant = false,
  wrapperClassName,
}: {
  isLinkVariant?: boolean;
  wrapperClassName?: string;
}) {
  const intl = useIntl();
  const brand = useBrandLoaderData();
  const { isVirtualStore } = useVirtualStore();
  const { userEffectiveProgram } = useProgramDetails();
  const {
    requiredToPurchase,
    requiredToPurchaseCount,
    requiredToPurchaseTotalCost,
    requiredToPurchaseDefaultVariantIds,
    requiredToPurchaseInCartCount,
    needsPurchase,
    purchaseCurrency,
    canAddAllToCart,
  } = useTermStoreMetrics();

  const { showToast } = useToasts();
  const [showConfetti, setShowConfetti] = useState(false);
  const fetcher = usePromiseFetcher<
    Awaited<Promise<ReturnType<typeof useStoreMultiCartFetcher>["data"]>>
  >({ waitForIdle: true });
  const navigate = useNavigate();
  const enabled = brand.VirtualStore.AddAllToCart.enabled;
  const confettiEnabled = brand.VirtualStore.AddAllToCart.confettiEnabled;

  const onClick = useCallback(() => {
    if (confettiEnabled) {
      setShowConfetti(true);
    }
    fetcher
      .submit(
        {
          variants: requiredToPurchaseDefaultVariantIds.map((id) => ({
            id,
          })),
        },
        {
          action: `/my-courses/store-multi-cart`,
          method: "POST",
          encType: "application/json",
        }
      )
      .then((response) => {
        const results = response.data;
        if (results) {
          const errors = results.filter(
            (result) => result.status === "rejected" || !result.value.ok
          );
          if (
            errors.length === requiredToPurchaseCount &&
            requiredToPurchaseCount > 3
          ) {
            // don't spam toast if something really drastic happens
            showToast(
              <Toast
                type="error"
                title={intl.formatMessage(
                  {
                    defaultMessage: "Unable to add items to cart.",
                  },
                  { count: requiredToPurchaseCount }
                )}
              />
            );
          } else {
            errors.forEach((error) => {
              const failedToCart = requiredToPurchase[results.indexOf(error)];
              const title = intl.formatMessage(
                { defaultMessage: "Could not add “{title}” to cart" },
                { title: failedToCart.title }
              );
              showToast(<Toast type={"error"} title={title} />);
            });
          }
        }
      })
      .catch(() => {});
  }, [
    confettiEnabled,
    fetcher,
    intl,
    requiredToPurchase,
    requiredToPurchaseCount,
    requiredToPurchaseDefaultVariantIds,
    showToast,
  ]);
  const isLoading = fetcher.state !== "idle";
  const programAllowsAddingAll =
    userEffectiveProgram === "no_program" ||
    userEffectiveProgram === "inclusive_access" ||
    userEffectiveProgram === "equitable_access";
  const isEligibleForAddAll =
    isVirtualStore &&
    enabled &&
    programAllowsAddingAll &&
    (requiredToPurchaseCount > 0 || requiredToPurchaseInCartCount > 0);

  useEffect(() => {
    if (isEligibleForAddAll && !canAddAllToCart) {
      logEvent("my_courses_add_all_to_cart_blocked", {
        host: window.location.host,
      });
    }
  }, [isEligibleForAddAll, canAddAllToCart]);

  if (!isEligibleForAddAll || !canAddAllToCart) {
    return null;
  }

  return (
    <>
      <Confetti
        trigger={showConfetti}
        onComplete={() => setShowConfetti(false)}
        description={intl.formatMessage({
          defaultMessage: "A celebratory confetti animation appeared",
        })}
      />
      <div className={clsx(wrapperClassName)}>
        {needsPurchase ? (
          <ProgramBannerButton
            key={"banner-add-all-button"}
            loading={isLoading}
            onClick={onClick}
            keepSameWidth={true}
            isLinkVariant={isLinkVariant}
          >
            <FormattedMessage
              defaultMessage="Add {count} Required to Cart ({cost})"
              values={{
                count: requiredToPurchaseCount,
                cost: (
                  <FormattedNumber
                    value={requiredToPurchaseTotalCost}
                    style="currency"
                    currency={purchaseCurrency}
                  />
                ),
              }}
            />
          </ProgramBannerButton>
        ) : (
          <ProgramBannerButton
            key={"banner-add-all-button"}
            onClick={() => {
              void navigate("/my-cart");
            }}
            isLinkVariant={isLinkVariant}
          >
            <FormattedMessage defaultMessage="View in Cart" />
          </ProgramBannerButton>
        )}
      </div>
    </>
  );
}
